import type { DatePreset, DemoCommand, PublicationReceipt, Signal } from '../../services/scmMockApi';

export const demoStorageKey = `sci:scm-mock:v1:${import.meta.env.VITE_SCI_API_BASE_URL || window.location.origin}:demo`;
export const chainPresets = [
    { label: '1 request → 1 transport', requestCount: 1, count: 1 },
    { label: '1 request → 3 transports', requestCount: 1, count: 3 },
    { label: '3 requests → 1 transport', requestCount: 3, count: 1 },
    { label: '2 requests → 3 transports', requestCount: 2, count: 3 },
] as const;
export const datePresets: { value: DatePreset; label: string }[] = [
    { value: 'standard', label: 'Next weekday' },
    { value: 'pickup_next_week', label: 'In 7 days' },
    { value: 'delivery_two_days_later', label: 'More delivery time' },
];
export const pickupPresets = [
    { value: 'matching', label: 'Matching pickup', shortLabel: 'As requested' },
    { value: 'two_hours_later', label: 'Pickup 2 hours later', shortLabel: '+2 hours' },
    { value: 'one_day_later', label: 'Pickup 1 day later', shortLabel: '+1 day' },
    { value: 'one_day_earlier', label: 'Pickup 1 day earlier', shortLabel: '−1 day' },
] as const;
export type Publication = { command: DemoCommand; receipt?: PublicationReceipt; error?: string };
export type EventChain = {
    id: string;
    name: string;
    createdAt: string;
    datePreset: DatePreset;
    count: number;
    requestCount: number;
    pickupPreset: DemoCommand['pickup_preset'];
    requestStatus?: DemoCommand['request_status'];
    publications: Record<string, Publication>;
};
export type DemoState = { version: 1; chains: EventChain[]; selectedId?: string; tab: Signal; nextOrdinal: number };
export const emptyDemoState = (): DemoState => ({ version: 1, chains: [], tab: 'request', nextOrdinal: 1 });
export const publicationKey = (signal: Signal, index = 1) =>
    signal === 'request' ? (index === 1 ? 'request' : `request:${index}`) : `transport:${index}`;
export const chainFrozen = (chain: EventChain) => Object.keys(chain.publications).length > 0;
export function chainName(ordinal: number): string {
    let label = '';
    let value = ordinal;
    while (value > 0) {
        value--;
        label = String.fromCharCode(65 + (value % 26)) + label;
        value = Math.floor(value / 26);
    }
    return `Event chain ${label}`;
}
export function commandFor(
    chain: EventChain,
    index = 1,
    pickup: DemoCommand['pickup_preset'] = 'matching',
    signal: Signal = 'transports'
): DemoCommand {
    return {
        request_id: chain.id,
        chain_created_at: chain.createdAt,
        date_preset: chain.datePreset,
        planned_transport_count: chain.count,
        transport_index: signal === 'transports' ? index : 1,
        planned_request_count: chain.requestCount,
        request_index: signal === 'request' ? index : 1,
        pickup_preset: pickup,
        request_status: chain.requestStatus ?? 'SENT',
    };
}
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';
const uuid = (value: unknown) =>
    typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
function validCommand(value: unknown, chain: EventChain): value is DemoCommand {
    return (
        isObject(value) &&
        value.request_id === chain.id &&
        value.chain_created_at === chain.createdAt &&
        value.date_preset === chain.datePreset &&
        value.planned_transport_count === chain.count &&
        (value.planned_request_count ?? 1) === chain.requestCount &&
        typeof (value.request_index ?? 1) === 'number' &&
        Number.isInteger(value.request_index ?? 1) &&
        Number(value.request_index ?? 1) >= 1 &&
        Number(value.request_index ?? 1) <= chain.requestCount &&
        typeof value.transport_index === 'number' &&
        Number.isInteger(value.transport_index) &&
        value.transport_index >= 1 &&
        value.transport_index <= chain.count &&
        (value.request_status === undefined ||
            ['DRAFT', 'SCHEDULED', 'SENT', 'MISSED'].includes(String(value.request_status))) &&
        (value.revision === undefined || (Number.isSafeInteger(value.revision) && Number(value.revision) > 0)) &&
        pickupPresets.some(preset => preset.value === value.pickup_preset)
    );
}
export function loadDemoState(): DemoState {
    const saved: unknown = JSON.parse(localStorage.getItem(demoStorageKey) || 'null');
    if (saved === null) {
        return emptyDemoState();
    }
    if (
        !isObject(saved) ||
        saved.version !== 1 ||
        !Array.isArray(saved.chains) ||
        !['request', 'transports'].includes(String(saved.tab)) ||
        !Number.isSafeInteger(saved.nextOrdinal) ||
        Number(saved.nextOrdinal) < 1
    ) {
        throw new Error('Saved demo controls could not be read.');
    }
    // Existing single-request chains and saved retry commands retain their original identities.
    for (const chain of saved.chains) {
        if (isObject(chain) && chain.requestCount === undefined) {
            chain.requestCount = 1;
        }
    }
    const valid = saved.chains.every((chain: unknown) => {
        if (
            !isObject(chain) ||
            !uuid(chain.id) ||
            typeof chain.name !== 'string' ||
            typeof chain.createdAt !== 'string' ||
            !Number.isFinite(Date.parse(chain.createdAt)) ||
            !datePresets.some(preset => preset.value === chain.datePreset) ||
            typeof chain.count !== 'number' ||
            ![1, 2, 3, 5].includes(chain.count) ||
            typeof chain.requestCount !== 'number' ||
            ![1, 2, 3, 4].includes(chain.requestCount) ||
            !pickupPresets.some(preset => preset.value === chain.pickupPreset) ||
            (chain.requestStatus !== undefined &&
                !['DRAFT', 'SCHEDULED', 'SENT', 'MISSED'].includes(String(chain.requestStatus))) ||
            !isObject(chain.publications)
        ) {
            return false;
        }
        return Object.entries(chain.publications).every(([key, publication]) => {
            if (
                !isObject(publication) ||
                !validCommand(publication.command, chain as EventChain) ||
                (key !== publicationKey('request', publication.command.request_index ?? 1) &&
                    key !== publicationKey('transports', publication.command.transport_index))
            ) {
                return false;
            }
            const receipt = publication.receipt;
            return (
                receipt === undefined ||
                (isObject(receipt) &&
                    receipt.status === 'published' &&
                    receipt.request_id === chain.id &&
                    uuid(receipt.entity_id) &&
                    uuid(receipt.event_id) &&
                    Number.isSafeInteger(receipt.revision) &&
                    Number(receipt.revision) > 0)
            );
        });
    });
    if (
        !valid ||
        new Set(saved.chains.map(chain => chain.id)).size !== saved.chains.length ||
        (saved.selectedId !== undefined && !saved.chains.some(chain => chain.id === saved.selectedId))
    ) {
        throw new Error('Saved demo controls could not be read.');
    }
    return saved as DemoState;
}
export function demoDates(chain: EventChain): { pickup: string; delivery: string } {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date(chain.createdAt));
    const part = (type: string) => parts.find(item => item.type === type)?.value;
    const anchor = new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
    const pickup = new Date(anchor);
    pickup.setUTCDate(pickup.getUTCDate() + (chain.datePreset === 'pickup_next_week' ? 7 : 1));
    if (chain.datePreset !== 'pickup_next_week') {
        while ([0, 6].includes(pickup.getUTCDay())) {
            pickup.setUTCDate(pickup.getUTCDate() + 1);
        }
    }
    const delivery = new Date(pickup);
    delivery.setUTCDate(delivery.getUTCDate() + (chain.datePreset === 'delivery_two_days_later' ? 3 : 1));
    return { pickup: pickup.toISOString().slice(0, 10), delivery: delivery.toISOString().slice(0, 10) };
}

export function demoPickup(chain: EventChain, preset: DemoCommand['pickup_preset']) {
    const pickup = new Date(`${demoDates(chain).pickup}T12:00:00Z`);
    if (preset === 'one_day_later') {
        pickup.setUTCDate(pickup.getUTCDate() + 1);
    }
    if (preset === 'one_day_earlier') {
        pickup.setUTCDate(pickup.getUTCDate() - 1);
    }
    return {
        date: pickup.toISOString().slice(0, 10),
        start: preset === 'two_hours_later' ? '11:00' : '09:00',
        end: preset === 'two_hours_later' ? '12:00' : '10:00',
    };
}

export const requestNeedsPublication = (chain: EventChain, index: number) => {
    const publication = chain.publications[publicationKey('request', index)];
    return !publication?.receipt || (publication.command.request_status ?? 'SENT') !== (chain.requestStatus ?? 'SENT');
};

export const publishedRequests = (chain: EventChain) =>
    Array.from({ length: chain.requestCount }, (_, index) => index + 1).filter(
        index => !requestNeedsPublication(chain, index)
    ).length;

export const publishedTransports = (chain: EventChain) =>
    Object.entries(chain.publications).filter(([key, value]) => key.startsWith('transport:') && value.receipt).length;
